import type { TabDeprecated } from "@oysterhr/ui"
import { useTabsDeprecated } from "@oysterhr/ui"
import type { CountryIsoCode } from "@oysterhr/schema"
import { idToStrongId } from "@oysterhr/common"
import { Header } from "./components/Header"
import { TabFor } from "./components/TabFor"
import { getHiringTemplateSelectionTabs, makeFilterAndSortActiveCardsByRelevance } from "./helpers"
import type { ListHiringTemplatesFragment } from "./components/ListHiringTemplates"
import { ListHiringTemplates } from "./components/ListHiringTemplates"
import { NewBlankHireButton } from "./components/NewBlankHireButton"
import { HiringTemplateTabs } from "./components/HiringTemplateTabs"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Outlet } from "react-router-dom"
import { gql } from "@apollo/client"

export const SelectHiringTemplate = ({
  employmentTemplates = [],
  oysterTemplates = [],
  companyId,
  countryCode,
  subscriptionPlanId,
  employmentId,
}: {
  employmentTemplates?: SelectHiringTemplateFragment[]
  oysterTemplates?: SelectHiringTemplateFragment[]
  companyId: `Company:${string}`
  countryCode: CountryIsoCode
  subscriptionPlanId?: string
  employmentId?: string
}) => {
  const { t } = useTranslation("hiring_templates/SelectHiringTemplate")

  const activeEmploymentTemplates = useMemo(
    () => makeFilterAndSortActiveCardsByRelevance(countryCode)(employmentTemplates),
    [countryCode, employmentTemplates],
  )

  const tabs = useMemo(
    () => getHiringTemplateSelectionTabs(t, activeEmploymentTemplates, oysterTemplates),
    [t, activeEmploymentTemplates, oysterTemplates],
  )
  const tabsConfig = useTabsDeprecated(tabs as unknown as TabDeprecated[])
  const companyStrongId = idToStrongId(companyId)
  const { currentTabIndex, goToTab } = tabsConfig

  // Employment templates with a null country code are global templates.
  // We only want to take into account templates for the same country code or global templates.
  const hasEmploymentTemplates = activeEmploymentTemplates.length > 0
  const hasOysterTemplates = oysterTemplates[0] !== undefined
  const hideTabSelection = !hasEmploymentTemplates || !hasOysterTemplates

  useEffect(() => {
    // Prevent going back to "all-templates" when opening a modal in a different tab.
    if (currentTabIndex === 0) return goToTab("all-templates", { replace: true })
  }, [goToTab, currentTabIndex])

  return (
    <div className="space-y-10">
      <Header />

      {/* Tabs + New Hire Button */}
      <div>
        <div className="space-y-10 xl:hidden">
          <NewBlankHireButton
            isFullWidth
            companyStrongId={companyStrongId}
            countryCode={countryCode}
            subscriptionPlanId={subscriptionPlanId}
            employmentId={employmentId}
          />
          {!hideTabSelection ? <HiringTemplateTabs tabsConfig={tabsConfig} /> : null}
        </div>
        <div className="hidden xl:block">
          <div className="flex flex-row items-center justify-between">
            {!hideTabSelection ? <HiringTemplateTabs tabsConfig={tabsConfig} /> : <div />}
            <NewBlankHireButton
              companyStrongId={companyStrongId}
              countryCode={countryCode}
              subscriptionPlanId={subscriptionPlanId}
              employmentId={employmentId}
            />
          </div>
        </div>
      </div>

      <TabFor tabs={tabs} currentIndex={currentTabIndex} fragment="all-templates">
        {hasEmploymentTemplates && (
          <ListHiringTemplates
            title={t("listTemplates.sections.myTemplates")}
            employmentTemplates={employmentTemplates}
            countryCode={countryCode}
            limit={3}
            onExpand={() => {
              goToTab("my-templates")
            }}
            subscriptionPlanId={subscriptionPlanId}
            employmentId={employmentId}
          />
        )}
        {hasOysterTemplates && (
          <ListHiringTemplates
            title={t("listTemplates.sections.oysterTemplates")}
            description={t("listTemplates.sections.oysterTemplatesDescription")}
            employmentTemplates={oysterTemplates}
            countryCode={countryCode}
            companyId={companyId}
            limit={3}
            onExpand={() => {
              goToTab("oyster-templates")
            }}
            subscriptionPlanId={subscriptionPlanId}
            employmentId={employmentId}
          />
        )}
      </TabFor>

      <TabFor tabs={tabs} currentIndex={currentTabIndex} fragment="my-templates">
        <ListHiringTemplates
          title={t("listTemplates.sections.myTemplates")}
          employmentTemplates={employmentTemplates}
          countryCode={countryCode}
          subscriptionPlanId={subscriptionPlanId}
          employmentId={employmentId}
        />
      </TabFor>

      <TabFor tabs={tabs} currentIndex={currentTabIndex} fragment="oyster-templates">
        <ListHiringTemplates
          title={t("listTemplates.sections.oysterTemplates")}
          employmentTemplates={oysterTemplates}
          countryCode={countryCode}
          subscriptionPlanId={subscriptionPlanId}
          employmentId={employmentId}
        />
      </TabFor>

      <Outlet />
    </div>
  )
}

SelectHiringTemplate.fragment = gql`
  fragment SelectHiringTemplate on EmploymentTemplate {
    ...ListHiringTemplates
  }
  ${ListHiringTemplates.fragment}
`

// @todo/codegen: Remove this type when we can use the generated type from the fragment
export type SelectHiringTemplateFragment = ListHiringTemplatesFragment
